import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { pathLabelMap } from '@/layouts/data/menuItems';

import { WORKSPACE_TAB_TITLE_UPDATED_EVENT, getWorkspaceProjectTabTitle } from '@/hooks/workspaceTabProjectTitleCache';

/**
 * useSyncedPageTabs — PageTabs 页签状态管理 Hook
 *
 * 功能：
 *  1. 根据路由自动创建/去重/持久化页签
 *  2. 自动捕获 location.state 并存入对应 tab 的 state 字段（localStorage 持久化）
 *  3. 切换页签时通过 navigate(key, { state }) 自动恢复 state
 *  4. 暴露 setTabState 供业务页面手动更新 state
 *
 * 返回值：
 *  { tabs, onTabsChange, setTabState }
 *
 * 使用示例：
 *
 *  // 1. 在 Layout 中使用（已接入 MainLayout / WorkspaceLayout 等）
 *  const { tabs, onTabsChange, setTabState } = useSyncedPageTabs();
 *  <PageTabs tabs={tabs} onTabsChange={onTabsChange} />
 *
 *  // 2. 业务页面读取 state（无需改动，自动恢复）
 *  const { state } = useLocation();
 *  // state.slug / state.memcard 等在切换页签回来后依然可用
 *
 *  // 3. 业务页面主动更新 tab state（如保存草稿中间态）
 *  setTabState('/selection/cases/123', { draft: formValues });
 *  // 函数式更新：
 *  setTabState('/selection/cases/123', prev => ({ ...prev, step: 2 }));
 *
 *  // 4. 关闭指定页签（从业务侧触发）
 *  import { closePageTabByKey } from '@/hooks/useSyncedPageTabs';
 *  closePageTabByKey('/some/path');
 */

const EXCLUDED_PATHS = new Set(['/login', '/403', '/permission', '/forgetpassword']);
const MAX_TABS = 10;
const LS_TABS = 'page_tabs';

/** 医项目工作空间（`/medproject/projects/:id/*`，不含列表与 new）在 PageTabs 中统一展示为该文案 */
export const MED_PROJECT_WORKSPACE_PAGE_TAB_LABEL = '医项目 · 管理项目';

/** 会议工作空间（`/conf/conferences/:id/*`，不含列表与 new / usage / labor-progress / service-provider）统一展示为该文案 */
export const CONFERENCE_WORKSPACE_PAGE_TAB_LABEL = '医会议 · 管理项目';
/** 医评议工作空间（`/selection/projects/:id/*`，不含列表与 new）统一展示为该文案 */
export const SELECTION_WORKSPACE_PAGE_TAB_LABEL = '医评议 · 管理项目';

/** 关闭指定 key 的 PageTab：业务侧（如新增页取消）派发事件，hook 内监听后从内存与 localStorage 同步移除。 */
export const PAGE_TAB_CLOSE_EVENT = 'page-tab:close';
export function closePageTabByKey(key) {
    if (typeof key !== 'string' || !key) return;
    window.dispatchEvent(new CustomEvent(PAGE_TAB_CLOSE_EVENT, { detail: { key } }));
}

function isPageTabValue(value) {
    if (typeof value !== 'object' || value === null) return false;
    if (typeof value.key !== 'string' || typeof value.label !== 'string') return false;
    return value.title == null || typeof value.title === 'string';
}

/**
 * @param {string} pathname
 */
function getMedProjectWorkspaceId(pathname) {
    const m = pathname.match(/^\/medproject\/projects\/(?!new$)([^/]+)/);
    return m?.[1] ?? null;
}

/**
 * @param {string} pathname
 */
function isMedProjectWorkspacePath(pathname) {
    return /^\/medproject\/projects\/(?!new$)([^/]+)/.test(pathname);
}

/**
 * @param {string} pathname
 */
function buildWorkspacePageTab(pathname) {
    const pid = getMedProjectWorkspaceId(pathname);
    if (pid == null) {
        return { key: pathname, label: MED_PROJECT_WORKSPACE_PAGE_TAB_LABEL };
    }
    const name = getWorkspaceProjectTabTitle(pid);
    return {
        key: pathname,
        label: MED_PROJECT_WORKSPACE_PAGE_TAB_LABEL,
        title: name || `项目 ${pid}`,
        workspaceId: pid,
    };
}

/**
 * 会议工作空间 ID 提取：`/conf/conferences/:id/*`
 * 排除 reserved 段：`new`（新增态）、`usage` / `labor-progress` / `service-provider`（MainLayout 顶层页）
 * @param {string} pathname
 */
function getConferenceWorkspaceId(pathname) {
    const m = pathname.match(/^\/conf\/conferences\/(?!new$|usage$|labor-progress$|service-provider$)([^/]+)/);
    return m?.[1] ?? null;
}

/**
 * @param {string} pathname
 */
function isConferenceWorkspacePath(pathname) {
    return /^\/conf\/conferences\/(?!new$|usage$|labor-progress$|service-provider$)([^/]+)/.test(pathname);
}

/**
 * @param {string} pathname
 */
function buildConferenceWorkspacePageTab(pathname) {
    const cid = getConferenceWorkspaceId(pathname);
    if (cid == null) {
        return { key: pathname, label: CONFERENCE_WORKSPACE_PAGE_TAB_LABEL };
    }
    const name = getWorkspaceProjectTabTitle(cid, 'conference');
    return {
        key: pathname,
        label: CONFERENCE_WORKSPACE_PAGE_TAB_LABEL,
        title: name || `项目 ${cid}`,
        workspaceId: cid,
    };
}

function getSelectionWorkspaceId(pathname) {
    const m = pathname.match(/^\/selection\/projects\/(?!new$)([^/]+)/);
    return m?.[1] ?? null;
}

function isSelectionWorkspacePath(pathname) {
    return /^\/selection\/projects\/(?!new$)([^/]+)/.test(pathname);
}

function buildSelectionWorkspacePageTab(pathname) {
    const pid = getSelectionWorkspaceId(pathname);
    if (pid == null) {
        return { key: pathname, label: SELECTION_WORKSPACE_PAGE_TAB_LABEL };
    }
    const name = getWorkspaceProjectTabTitle(pid, 'selection');
    return {
        key: pathname,
        label: SELECTION_WORKSPACE_PAGE_TAB_LABEL,
        title: name || `项目 ${pid}`,
        workspaceId: pid,
    };
}

/** /selection/cases/edit/:id */
function isSelectionCaseEditPath(pathname) {
    return /^\/selection\/cases\/edit\/[^/]+\/?$/.test(pathname);
}

/** /selection/cases/:id（排除 edit） */
function isSelectionCaseDetailPath(pathname) {
    if (isSelectionCaseEditPath(pathname)) return false;
    return /^\/selection\/cases\/[^/]+\/?$/.test(pathname);
}

function isMedProjectDetailPath(pathname) {
    return /^\/medproject\/projects\/[^/]+\/.+/.test(pathname);
}

function isMedConnectDetailPath(pathname) {
    return /^\/medconnect\/projects\/[^/]+\/.+/.test(pathname);
}

/** /invitation/g/:gid/invitations */
function isInvitationListPath(pathname) {
    return /^\/invitation\/g\/[^/]+\/invitations\/?$/.test(pathname);
}

/** /invitation/g/:gid/invitations/:id/users */
function isInvitationUsersPath(pathname) {
    return /^\/invitation\/g\/[^/]+\/invitations\/[^/]+\/users\/?$/.test(pathname);
}

/** /invitation/g/:gid/invitations/new */
function isInvitationNewPath(pathname) {
    return /^\/invitation\/g\/[^/]+\/invitations\/new\/?$/.test(pathname);
}

/** /invitation/g/:gid/invitations/:id（排除 new 与 /users） */
function isInvitationEditPath(pathname) {
    if (isInvitationNewPath(pathname)) return false;
    if (isInvitationUsersPath(pathname)) return false;
    return /^\/invitation\/g\/[^/]+\/invitations\/[^/]+\/?$/.test(pathname);
}

/** /enterprise/staff/new */
function isEnterpriseStaffNewPath(pathname) {
    return pathname === '/enterprise/staff/new';
}

/** /enterprise/staff/:id（排除 new） */
function isEnterpriseStaffEditPath(pathname) {
    return /^\/enterprise\/staff\/(?!new$)[^/]+\/?$/.test(pathname);
}

function isEnterpriseStaffFormPath(pathname) {
    return isEnterpriseStaffNewPath(pathname) || isEnterpriseStaffEditPath(pathname);
}

function resolveEnterpriseStaffFormLabel(state, pathname) {
    if (state?.tip === 'edit' || isEnterpriseStaffEditPath(pathname)) {
        return '编辑员工';
    }
    return '新增员工';
}

function extractStaffNameFromState(state) {
    if (state == null || typeof state !== 'object') return undefined;
    const name = state.name;
    return typeof name === 'string' && name.trim() ? name.trim() : undefined;
}

/**
 * @param {string} pathname
 * @param {object | undefined | null} [state]
 * @returns {{ key: string, label: string, title?: string }}
 */
function buildEnterpriseStaffFormTab(pathname, state) {
    const label = resolveEnterpriseStaffFormLabel(state, pathname);
    const name = extractStaffNameFromState(state);
    return name ? { key: pathname, label, title: name } : { key: pathname, label };
}

/** /wj/surveys/new/list */
function isWjSurveyNewListPath(pathname) {
    return pathname === '/wj/surveys/new/list';
}

/** /wj/surveys/new/create|logic|setting|... */
function isWjSurveyNewEditPath(pathname) {
    return /^\/wj\/surveys\/new\/(create|logic|setting|whitelists|FinalCustom|facade)\/?$/.test(pathname);
}

/** /wj/surveys/edit/:id */
function isWjSurveyEditByIdPath(pathname) {
    return /^\/wj\/surveys\/edit\/[^/]+\/?$/.test(pathname);
}

function isWjSurveyEditPreviewPath(pathname) {
    return /^\/wj\/surveys\/[^/]+\/preview\/?$/.test(pathname);
}

function isWjSurveySharePath(pathname) {
    return /^\/wj\/surveys\/[^/]+\/share\/?$/.test(pathname);
}

/** 问卷编辑/发布流程（同一问卷共用一个 Tab） */
function isWjSurveyEditFlowPath(pathname) {
    return isWjSurveyNewEditPath(pathname) || isWjSurveyEditByIdPath(pathname) || isWjSurveyEditPreviewPath(pathname) || isWjSurveySharePath(pathname);
}

/** 问卷数据统计子页（同一问卷共用一个 Tab） */
function isWjSurveyDataPath(pathname) {
    return /^\/wj\/surveys\/[^/]+\/(charts|check|report|details|checkdetails|answer|customreport)\/?$/.test(pathname);
}

function getWjSurveyIdFromDataPath(pathname) {
    const m = pathname.match(/^\/wj\/surveys\/([^/]+)\/(charts|check|report|details|checkdetails|answer|customreport)\/?$/);
    return m?.[1] ?? null;
}

function getWjSurveyEditFlowIdFromPathname(pathname) {
    if (isWjSurveyEditByIdPath(pathname)) {
        return pathname.match(/^\/wj\/surveys\/edit\/([^/]+)/)?.[1] ?? null;
    }
    if (isWjSurveyEditPreviewPath(pathname) || isWjSurveySharePath(pathname)) {
        return pathname.match(/^\/wj\/surveys\/([^/]+)\/(preview|share)/)?.[1] ?? null;
    }
    return null;
}

function getWjSurveyEditFlowIdFromTab(tab) {
    const fromPath = getWjSurveyEditFlowIdFromPathname(tab.key);
    if (fromPath) return fromPath;
    if (tab.state?.id != null) return String(tab.state.id);
    return null;
}

/**
 * @param {string} pathname
 * @param {object | undefined | null} locationState
 */
function getWjSurveyEditFlowId(pathname, locationState) {
    const fromPath = getWjSurveyEditFlowIdFromPathname(pathname);
    if (fromPath) return fromPath;
    if (locationState?.id != null) return String(locationState.id);
    return null;
}

function extractSurveyNameFromState(state) {
    if (state == null || typeof state !== 'object') return undefined;
    const name = state.name ?? state.title;
    return typeof name === 'string' && name.trim() ? name.trim() : undefined;
}

function resolveWjSurveyPageLabel(pathname) {
    if (isWjSurveyNewListPath(pathname)) return '创建问卷';
    if (isWjSurveyNewEditPath(pathname) || isWjSurveyEditByIdPath(pathname)) return '编辑问卷';
    if (isWjSurveyEditPreviewPath(pathname)) return '预览问卷';
    if (isWjSurveySharePath(pathname)) return '分享问卷';
    if (!isWjSurveyDataPath(pathname)) return null;
    const seg = pathname.match(/^\/wj\/surveys\/[^/]+\/([^/]+)/)?.[1];
    switch (seg) {
        case 'charts':
            return '统计图表';
        case 'check':
            return '作答详情';
        case 'report':
        case 'customreport':
            return '调研报告';
        case 'details':
        case 'checkdetails':
            return '数据详情';
        case 'answer':
            return '查看答卷';
        default:
            return null;
    }
}

/**
 * @param {string} pathname
 * @param {object | undefined | null} [state]
 * @returns {{ key: string, label: string, title?: string }}
 */
function buildWjSurveyPageTab(pathname, state) {
    const label = resolveWjSurveyPageLabel(pathname) ?? '问卷';
    const name = extractSurveyNameFromState(state);
    return name ? { key: pathname, label, title: name } : { key: pathname, label };
}

function buildWjSurveyEditFlowTab(pathname, state) {
    return buildWjSurveyPageTab(pathname, state);
}

function buildWjSurveyDataTab(pathname, state) {
    return buildWjSurveyPageTab(pathname, state);
}

/**
 * @param {{ key: string, state?: object }[]} tabs
 * @param {string} pathname
 * @param {object | undefined | null} locationState
 */
function findWjSurveyEditFlowTabIndex(tabs, pathname, locationState) {
    const targetId = getWjSurveyEditFlowId(pathname, locationState);
    if (targetId) {
        return tabs.findIndex(t => isWjSurveyEditFlowPath(t.key) && getWjSurveyEditFlowIdFromTab(t) === targetId);
    }
    return tabs.findIndex(t => isWjSurveyNewEditPath(t.key) && !getWjSurveyEditFlowIdFromTab(t));
}

function findWjSurveyDataTabIndex(tabs, pathname) {
    const targetId = getWjSurveyIdFromDataPath(pathname);
    if (!targetId) return -1;
    return tabs.findIndex(t => isWjSurveyDataPath(t.key) && getWjSurveyIdFromDataPath(t.key) === targetId);
}

/**
 * @param {{ key: string }[]} tabs
 * @param {string} pathname
 * @param {object | undefined | null} locationState
 */
function hasWjSurveyTabForCurrentPath(tabs, pathname, locationState) {
    if (isWjSurveyNewListPath(pathname)) {
        return tabs.some(t => isWjSurveyNewListPath(t.key));
    }
    if (isWjSurveyEditFlowPath(pathname)) {
        return findWjSurveyEditFlowTabIndex(tabs, pathname, locationState) >= 0;
    }
    if (isWjSurveyDataPath(pathname)) {
        return findWjSurveyDataTabIndex(tabs, pathname) >= 0;
    }
    return false;
}

function dedupeWjSurveyNewListTab(tabs) {
    const firstIdx = tabs.findIndex(t => isWjSurveyNewListPath(t.key));
    if (firstIdx === -1) return tabs;
    const last = [...tabs].reverse().find(t => isWjSurveyNewListPath(t.key));
    const stripped = tabs.filter(t => !isWjSurveyNewListPath(t.key));
    const merged = buildWjSurveyPageTab(last.key, last.state);
    return [...stripped.slice(0, firstIdx), { ...merged, state: last.state }, ...stripped.slice(firstIdx)];
}

function orderedWjSurveyEditFlowIds(tabs) {
    const ids = [];
    const seen = new Set();
    for (const t of tabs) {
        if (!isWjSurveyEditFlowPath(t.key)) continue;
        const id = getWjSurveyEditFlowIdFromTab(t);
        if (id && !seen.has(id)) {
            seen.add(id);
            ids.push(id);
        }
    }
    return ids;
}

function dedupeWjSurveyEditFlowForId(tabs, surveyId) {
    const idStr = String(surveyId);
    const firstIdx = tabs.findIndex(t => isWjSurveyEditFlowPath(t.key) && getWjSurveyEditFlowIdFromTab(t) === idStr);
    if (firstIdx === -1) return tabs;

    let lastVisited = tabs[firstIdx];
    for (let i = firstIdx + 1; i < tabs.length; i++) {
        const t = tabs[i];
        if (isWjSurveyEditFlowPath(t.key) && getWjSurveyEditFlowIdFromTab(t) === idStr) {
            lastVisited = t;
        }
    }

    const rest = tabs.slice(0, firstIdx).concat(tabs.slice(firstIdx + 1).filter(t => !(isWjSurveyEditFlowPath(t.key) && getWjSurveyEditFlowIdFromTab(t) === idStr)));
    const merged = buildWjSurveyEditFlowTab(lastVisited.key, lastVisited.state);
    return [...rest.slice(0, firstIdx), { ...merged, state: lastVisited.state }, ...rest.slice(firstIdx)];
}

function dedupeWjSurveyEditFlowWithoutId(tabs) {
    const firstIdx = tabs.findIndex(t => isWjSurveyNewEditPath(t.key) && !getWjSurveyEditFlowIdFromTab(t));
    if (firstIdx === -1) return tabs;
    const last = [...tabs].reverse().find(t => isWjSurveyNewEditPath(t.key) && !getWjSurveyEditFlowIdFromTab(t));
    const stripped = tabs.filter(t => !(isWjSurveyNewEditPath(t.key) && !getWjSurveyEditFlowIdFromTab(t)));
    const merged = buildWjSurveyEditFlowTab(last.key, last.state);
    return [...stripped.slice(0, firstIdx), { ...merged, state: last.state }, ...stripped.slice(firstIdx)];
}

function dedupeWjSurveyEditFlowTabs(tabs) {
    let next = tabs;
    for (const id of orderedWjSurveyEditFlowIds(tabs)) {
        next = dedupeWjSurveyEditFlowForId(next, id);
    }
    return dedupeWjSurveyEditFlowWithoutId(next);
}

function orderedWjSurveyDataIds(tabs) {
    const ids = [];
    const seen = new Set();
    for (const t of tabs) {
        const id = getWjSurveyIdFromDataPath(t.key);
        if (id && isWjSurveyDataPath(t.key) && !seen.has(id)) {
            seen.add(id);
            ids.push(id);
        }
    }
    return ids;
}

function dedupeWjSurveyDataForId(tabs, surveyId) {
    const idStr = String(surveyId);
    const firstIdx = tabs.findIndex(t => isWjSurveyDataPath(t.key) && getWjSurveyIdFromDataPath(t.key) === idStr);
    if (firstIdx === -1) return tabs;

    let lastVisited = tabs[firstIdx];
    for (let i = firstIdx + 1; i < tabs.length; i++) {
        const t = tabs[i];
        if (isWjSurveyDataPath(t.key) && getWjSurveyIdFromDataPath(t.key) === idStr) {
            lastVisited = t;
        }
    }

    const rest = tabs.slice(0, firstIdx).concat(tabs.slice(firstIdx + 1).filter(t => !(isWjSurveyDataPath(t.key) && getWjSurveyIdFromDataPath(t.key) === idStr)));
    const merged = buildWjSurveyDataTab(lastVisited.key, lastVisited.state);
    return [...rest.slice(0, firstIdx), { ...merged, state: lastVisited.state }, ...rest.slice(firstIdx)];
}

function dedupeWjSurveyDataTabs(tabs) {
    let next = tabs;
    for (const id of orderedWjSurveyDataIds(tabs)) {
        next = dedupeWjSurveyDataForId(next, id);
    }
    return next;
}

/** @returns {{ label: string, title?: string } | null} */
function resolveProjectWorkspaceTabMeta(pathname) {
    if (!isMedProjectWorkspacePath(pathname)) return null;
    return buildWorkspacePageTab(pathname);
}

/** @returns {{ label: string, title?: string } | null} */
function resolveConferenceWorkspaceTabMeta(pathname) {
    if (!isConferenceWorkspacePath(pathname)) return null;
    return buildConferenceWorkspacePageTab(pathname);
}

function resolveSelectionWorkspaceTabMeta(pathname) {
    if (!isSelectionWorkspacePath(pathname)) return null;
    return buildSelectionWorkspacePageTab(pathname);
}

function resolveLabelForPathname(pathname) {
    if (pathname === '/medproject/projects/new') {
        return { label: '新增医项目' };
    }
    if (pathname === '/conf/conferences/new') {
        return { label: '新增会议项目' };
    }
    if (pathname === '/selection/projects/new') {
        return { label: '新增评议项目' };
    }
    if (pathname === '/uc/user-groups/new') {
        return { label: '新增用户分群' };
    }
    if (/^\/uc\/user-groups\/(?!new$)[^/]+\/?$/.test(pathname)) {
        return { label: '用户分群详情' };
    }
    if (isEnterpriseStaffNewPath(pathname)) {
        return { label: '新增员工' };
    }
    if (isEnterpriseStaffEditPath(pathname)) {
        return { label: '编辑员工' };
    }
    const ws = resolveProjectWorkspaceTabMeta(pathname);
    if (ws) return ws;
    const cws = resolveConferenceWorkspaceTabMeta(pathname);
    if (cws) return cws;
    const sws = resolveSelectionWorkspaceTabMeta(pathname);
    if (sws) return sws;
    if (pathLabelMap[pathname]) {
        return { label: pathLabelMap[pathname] };
    }
    const wjLabel = resolveWjSurveyPageLabel(pathname);
    if (wjLabel) {
        return { label: wjLabel };
    }
    if (isInvitationNewPath(pathname)) {
        return { label: '新增邀请函' };
    }
    if (isInvitationEditPath(pathname)) {
        return { label: '编辑邀请函' };
    }
    if (isInvitationUsersPath(pathname)) {
        return { label: '邀请用户管理' };
    }
    if (isInvitationListPath(pathname)) {
        return { label: '邀请函管理' };
    }
    if (isSelectionCaseEditPath(pathname)) {
        return { label: '编辑实践' };
    }
    if (isSelectionCaseDetailPath(pathname)) {
        return { label: '实践详情' };
    }
    if (isMedProjectDetailPath(pathname)) {
        return { label: '医项目详情' };
    }
    if (isMedConnectDetailPath(pathname)) {
        return { label: '医患通详情' };
    }
    if (/^\/healthaccount\/eha\/solutions\/edit\//.test(pathname)) {
        return { label: '编辑解决方案' };
    }
    if (/^\/healthaccount\/eha\/solutions\/placard\//.test(pathname)) {
        return { label: '生成壁报' };
    }
    const seg = pathname.split('/').filter(Boolean).at(-1) ?? pathname;
    return { label: seg.charAt(0).toUpperCase() + seg.slice(1) };
}

/**
 * 同一医项目 workspace 下只保留一个页签，key 为最近访问子路径。
 * @param {string} projectId
 */
function dedupeWorkspaceForProjectId(tabs, projectId) {
    const idStr = String(projectId);
    const firstIdx = tabs.findIndex(t => isMedProjectWorkspacePath(t.key) && getMedProjectWorkspaceId(t.key) === idStr);
    if (firstIdx === -1) return tabs;

    let lastVisited = tabs[firstIdx];
    for (let i = firstIdx + 1; i < tabs.length; i++) {
        const t = tabs[i];
        if (isMedProjectWorkspacePath(t.key) && getMedProjectWorkspaceId(t.key) === idStr) {
            lastVisited = t;
        }
    }

    const rest = tabs.slice(0, firstIdx).concat(tabs.slice(firstIdx + 1).filter(t => !(isMedProjectWorkspacePath(t.key) && getMedProjectWorkspaceId(t.key) === idStr)));

    const merged = buildWorkspacePageTab(lastVisited.key);
    return [...rest.slice(0, firstIdx), merged, ...rest.slice(firstIdx)];
}

function orderedWorkspaceProjectIds(tabs) {
    const ids = [];
    const seen = new Set();
    for (const t of tabs) {
        const pid = getMedProjectWorkspaceId(t.key);
        if (pid && isMedProjectWorkspacePath(t.key) && !seen.has(pid)) {
            seen.add(pid);
            ids.push(pid);
        }
    }
    return ids;
}

function dedupeMedProjectWorkspaceTabs(tabs) {
    let next = tabs;
    for (const pid of orderedWorkspaceProjectIds(tabs)) {
        next = dedupeWorkspaceForProjectId(next, pid);
    }
    return next;
}

/**
 * 同一会议 workspace 下只保留一个页签。
 * @param {string} conferenceId
 */
function dedupeWorkspaceForConferenceId(tabs, conferenceId) {
    const idStr = String(conferenceId);
    const firstIdx = tabs.findIndex(t => isConferenceWorkspacePath(t.key) && getConferenceWorkspaceId(t.key) === idStr);
    if (firstIdx === -1) return tabs;

    let lastVisited = tabs[firstIdx];
    for (let i = firstIdx + 1; i < tabs.length; i++) {
        const t = tabs[i];
        if (isConferenceWorkspacePath(t.key) && getConferenceWorkspaceId(t.key) === idStr) {
            lastVisited = t;
        }
    }

    const rest = tabs.slice(0, firstIdx).concat(tabs.slice(firstIdx + 1).filter(t => !(isConferenceWorkspacePath(t.key) && getConferenceWorkspaceId(t.key) === idStr)));

    const merged = buildConferenceWorkspacePageTab(lastVisited.key);
    return [...rest.slice(0, firstIdx), merged, ...rest.slice(firstIdx)];
}

function orderedWorkspaceConferenceIds(tabs) {
    const ids = [];
    const seen = new Set();
    for (const t of tabs) {
        const cid = getConferenceWorkspaceId(t.key);
        if (cid && isConferenceWorkspacePath(t.key) && !seen.has(cid)) {
            seen.add(cid);
            ids.push(cid);
        }
    }
    return ids;
}

function dedupeConferenceWorkspaceTabs(tabs) {
    let next = tabs;
    for (const cid of orderedWorkspaceConferenceIds(tabs)) {
        next = dedupeWorkspaceForConferenceId(next, cid);
    }
    return next;
}

function dedupeWorkspaceForSelectionId(tabs, projectId) {
    const idStr = String(projectId);
    const firstIdx = tabs.findIndex(t => isSelectionWorkspacePath(t.key) && getSelectionWorkspaceId(t.key) === idStr);
    if (firstIdx === -1) return tabs;

    let lastVisited = tabs[firstIdx];
    for (let i = firstIdx + 1; i < tabs.length; i++) {
        const t = tabs[i];
        if (isSelectionWorkspacePath(t.key) && getSelectionWorkspaceId(t.key) === idStr) {
            lastVisited = t;
        }
    }

    const rest = tabs.slice(0, firstIdx).concat(tabs.slice(firstIdx + 1).filter(t => !(isSelectionWorkspacePath(t.key) && getSelectionWorkspaceId(t.key) === idStr)));
    const merged = buildSelectionWorkspacePageTab(lastVisited.key);
    return [...rest.slice(0, firstIdx), merged, ...rest.slice(firstIdx)];
}

function orderedWorkspaceSelectionIds(tabs) {
    const ids = [];
    const seen = new Set();
    for (const t of tabs) {
        const pid = getSelectionWorkspaceId(t.key);
        if (pid && isSelectionWorkspacePath(t.key) && !seen.has(pid)) {
            seen.add(pid);
            ids.push(pid);
        }
    }
    return ids;
}

function dedupeSelectionWorkspaceTabs(tabs) {
    let next = tabs;
    for (const pid of orderedWorkspaceSelectionIds(tabs)) {
        next = dedupeWorkspaceForSelectionId(next, pid);
    }
    return next;
}

function dedupeDynamicDetailTabs(tabs) {
    const firstCaseEditIdx = tabs.findIndex(t => isSelectionCaseEditPath(t.key));
    if (firstCaseEditIdx >= 0) {
        const last = [...tabs].reverse().find(t => isSelectionCaseEditPath(t.key));
        const stripped = tabs.filter(t => !isSelectionCaseEditPath(t.key));
        tabs = [...stripped.slice(0, firstCaseEditIdx), last, ...stripped.slice(firstCaseEditIdx)];
    }
    const firstCaseDetailIdx = tabs.findIndex(t => isSelectionCaseDetailPath(t.key));
    if (firstCaseDetailIdx >= 0) {
        const last = [...tabs].reverse().find(t => isSelectionCaseDetailPath(t.key));
        const stripped = tabs.filter(t => !isSelectionCaseDetailPath(t.key));
        tabs = [...stripped.slice(0, firstCaseDetailIdx), last, ...stripped.slice(firstCaseDetailIdx)];
    }
    const firstMedIdx = tabs.findIndex(t => isMedProjectDetailPath(t.key));
    if (firstMedIdx >= 0) {
        const lastMed = [...tabs].reverse().find(t => isMedProjectDetailPath(t.key));
        const stripped = tabs.filter(t => !isMedProjectDetailPath(t.key));
        tabs = [...stripped.slice(0, firstMedIdx), lastMed, ...stripped.slice(firstMedIdx)];
    }
    const firstConnectIdx = tabs.findIndex(t => isMedConnectDetailPath(t.key));
    if (firstConnectIdx >= 0) {
        const lastConnect = [...tabs].reverse().find(t => isMedConnectDetailPath(t.key));
        const stripped = tabs.filter(t => !isMedConnectDetailPath(t.key));
        tabs = [...stripped.slice(0, firstConnectIdx), lastConnect, ...stripped.slice(firstConnectIdx)];
    }
    const firstInviteListIdx = tabs.findIndex(t => isInvitationListPath(t.key));
    if (firstInviteListIdx >= 0) {
        const last = [...tabs].reverse().find(t => isInvitationListPath(t.key));
        const stripped = tabs.filter(t => !isInvitationListPath(t.key));
        tabs = [...stripped.slice(0, firstInviteListIdx), last, ...stripped.slice(firstInviteListIdx)];
    }
    const firstInviteUsersIdx = tabs.findIndex(t => isInvitationUsersPath(t.key));
    if (firstInviteUsersIdx >= 0) {
        const last = [...tabs].reverse().find(t => isInvitationUsersPath(t.key));
        const stripped = tabs.filter(t => !isInvitationUsersPath(t.key));
        tabs = [...stripped.slice(0, firstInviteUsersIdx), last, ...stripped.slice(firstInviteUsersIdx)];
    }
    const firstInviteNewIdx = tabs.findIndex(t => isInvitationNewPath(t.key));
    if (firstInviteNewIdx >= 0) {
        const last = [...tabs].reverse().find(t => isInvitationNewPath(t.key));
        const stripped = tabs.filter(t => !isInvitationNewPath(t.key));
        tabs = [...stripped.slice(0, firstInviteNewIdx), last, ...stripped.slice(firstInviteNewIdx)];
    }
    const firstInviteEditIdx = tabs.findIndex(t => isInvitationEditPath(t.key));
    if (firstInviteEditIdx >= 0) {
        const last = [...tabs].reverse().find(t => isInvitationEditPath(t.key));
        const stripped = tabs.filter(t => !isInvitationEditPath(t.key));
        tabs = [...stripped.slice(0, firstInviteEditIdx), last, ...stripped.slice(firstInviteEditIdx)];
    }
    tabs = dedupeWjSurveyNewListTab(tabs);
    tabs = dedupeWjSurveyEditFlowTabs(tabs);
    tabs = dedupeWjSurveyDataTabs(tabs);
    return dedupeSelectionWorkspaceTabs(dedupeConferenceWorkspaceTabs(dedupeMedProjectWorkspaceTabs(tabs)));
}

/** 刷新已存在 workspace 页签的 label/title（项目名称更新后）。 */
function reapplyWorkspaceTabMeta(tabs) {
    let changed = false;
    const next = tabs.map(t => {
        if (isMedProjectWorkspacePath(t.key)) {
            const built = buildWorkspacePageTab(t.key);
            const sameLabel = built.label === t.label;
            const sameTitle = (built.title ?? '') === (t.title ?? '');
            if (sameLabel && sameTitle && built.key === t.key) return t;
            changed = true;
            return { ...t, label: built.label, title: built.title };
        }
        if (isConferenceWorkspacePath(t.key)) {
            const built = buildConferenceWorkspacePageTab(t.key);
            const sameLabel = built.label === t.label;
            const sameTitle = (built.title ?? '') === (t.title ?? '');
            if (sameLabel && sameTitle && built.key === t.key) return t;
            changed = true;
            return { ...t, label: built.label, title: built.title };
        }
        if (isSelectionWorkspacePath(t.key)) {
            const built = buildSelectionWorkspacePageTab(t.key);
            const sameLabel = built.label === t.label;
            const sameTitle = (built.title ?? '') === (t.title ?? '');
            if (sameLabel && sameTitle && built.key === t.key) return t;
            changed = true;
            return { ...t, label: built.label, title: built.title };
        }
        return t;
    });
    return { next, changed };
}

function readTabs() {
    try {
        const raw = JSON.parse(localStorage.getItem(LS_TABS) ?? '[]');
        if (!Array.isArray(raw)) return [];
        const mapped = raw.filter(isPageTabValue).map(t => {
            // 保留已持久化的 state
            const savedState = t.state != null && typeof t.state === 'object' ? t.state : undefined;
            const pid = getMedProjectWorkspaceId(t.key);
            if (pid && isMedProjectWorkspacePath(t.key)) {
                const built = buildWorkspacePageTab(t.key);
                const tab = { key: t.key, label: built.label, title: built.title };
                return savedState ? { ...tab, state: savedState } : tab;
            }
            const cid = getConferenceWorkspaceId(t.key);
            if (cid && isConferenceWorkspacePath(t.key)) {
                const built = buildConferenceWorkspacePageTab(t.key);
                const tab = { key: t.key, label: built.label, title: built.title };
                return savedState ? { ...tab, state: savedState } : tab;
            }
            const sid = getSelectionWorkspaceId(t.key);
            if (sid && isSelectionWorkspacePath(t.key)) {
                const built = buildSelectionWorkspacePageTab(t.key);
                const tab = { key: t.key, label: built.label, title: built.title };
                return savedState ? { ...tab, state: savedState } : tab;
            }
            if (isWjSurveyNewListPath(t.key) || isWjSurveyEditFlowPath(t.key) || isWjSurveyDataPath(t.key)) {
                const built = buildWjSurveyPageTab(t.key, savedState);
                const tab = built.title != null ? built : { key: built.key, label: built.label };
                return savedState ? { ...tab, state: savedState } : tab;
            }
            if (isEnterpriseStaffFormPath(t.key)) {
                const built = buildEnterpriseStaffFormTab(t.key, savedState);
                const tab = built.title != null ? built : { key: built.key, label: built.label };
                return savedState ? { ...tab, state: savedState } : tab;
            }
            const { label, title } = resolveLabelForPathname(t.key);
            const tab = title != null ? { key: t.key, label, title } : { key: t.key, label };
            return savedState ? { ...tab, state: savedState } : tab;
        });
        return dedupeDynamicDetailTabs(mapped);
    } catch {
        return [];
    }
}

function safeLsSet(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch {
        // quota exceeded or private-browsing mode
    }
}

/** 超出上限时反复移除首个「非当前路由」页签，直至数量合规（与路由同步、持久化一致） */
function trimToMaxTabs(next, currentPath) {
    let result = next;
    while (result.length > MAX_TABS) {
        const removeIdx = result.findIndex(t => t.key !== currentPath);
        if (removeIdx === -1) break;
        result = result.filter((_, i) => i !== removeIdx);
    }
    return result;
}

function withMaxTabs(next, currentPath) {
    return trimToMaxTabs(next, currentPath);
}

function nextTabsForPathname(prev, pathname) {
    if (EXCLUDED_PATHS.has(pathname)) return prev;
    const wsId = getMedProjectWorkspaceId(pathname);
    if (wsId && isMedProjectWorkspacePath(pathname) && prev.some(t => isMedProjectWorkspacePath(t.key) && getMedProjectWorkspaceId(t.key) === wsId)) {
        return prev;
    }
    const cwsId = getConferenceWorkspaceId(pathname);
    if (cwsId && isConferenceWorkspacePath(pathname) && prev.some(t => isConferenceWorkspacePath(t.key) && getConferenceWorkspaceId(t.key) === cwsId)) {
        return prev;
    }
    const swsId = getSelectionWorkspaceId(pathname);
    if (swsId && isSelectionWorkspacePath(pathname) && prev.some(t => isSelectionWorkspacePath(t.key) && getSelectionWorkspaceId(t.key) === swsId)) {
        return prev;
    }
    if (prev.some(t => t.key === pathname)) return prev;

    if (isSelectionCaseEditPath(pathname)) {
        const firstIdx = prev.findIndex(t => isSelectionCaseEditPath(t.key));
        const stripped = prev.filter(t => !isSelectionCaseEditPath(t.key));
        const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
        const tab = { key: pathname, label: '编辑实践' };
        const next = withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname);
        safeLsSet(LS_TABS, JSON.stringify(next));
        return next;
    }

    if (isSelectionCaseDetailPath(pathname)) {
        const firstIdx = prev.findIndex(t => isSelectionCaseDetailPath(t.key));
        const stripped = prev.filter(t => !isSelectionCaseDetailPath(t.key));
        const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
        const tab = { key: pathname, label: '实践详情' };
        const next = withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname);
        safeLsSet(LS_TABS, JSON.stringify(next));
        return next;
    }

    if (isMedProjectDetailPath(pathname)) {
        const firstIdx = prev.findIndex(t => isMedProjectDetailPath(t.key));
        const stripped = prev.filter(t => !isMedProjectDetailPath(t.key));
        const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
        const tab = { key: pathname, label: '医项目详情' };
        const next = withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname);
        safeLsSet(LS_TABS, JSON.stringify(next));
        return next;
    }

    if (isMedConnectDetailPath(pathname)) {
        const firstIdx = prev.findIndex(t => isMedConnectDetailPath(t.key));
        const stripped = prev.filter(t => !isMedConnectDetailPath(t.key));
        const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
        const tab = { key: pathname, label: '医患通详情' };
        const next = withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname);
        safeLsSet(LS_TABS, JSON.stringify(next));
        return next;
    }

    if (isInvitationNewPath(pathname)) {
        const firstIdx = prev.findIndex(t => isInvitationNewPath(t.key));
        const stripped = prev.filter(t => !isInvitationNewPath(t.key));
        const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
        const tab = { key: pathname, label: '新增邀请函' };
        const next = withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname);
        safeLsSet(LS_TABS, JSON.stringify(next));
        return next;
    }

    if (isInvitationEditPath(pathname)) {
        const firstIdx = prev.findIndex(t => isInvitationEditPath(t.key));
        const stripped = prev.filter(t => !isInvitationEditPath(t.key));
        const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
        const tab = { key: pathname, label: '编辑邀请函' };
        const next = withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname);
        safeLsSet(LS_TABS, JSON.stringify(next));
        return next;
    }

    if (isInvitationUsersPath(pathname)) {
        const firstIdx = prev.findIndex(t => isInvitationUsersPath(t.key));
        const stripped = prev.filter(t => !isInvitationUsersPath(t.key));
        const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
        const tab = { key: pathname, label: '邀请用户管理' };
        const next = withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname);
        safeLsSet(LS_TABS, JSON.stringify(next));
        return next;
    }

    if (isInvitationListPath(pathname)) {
        const firstIdx = prev.findIndex(t => isInvitationListPath(t.key));
        const stripped = prev.filter(t => !isInvitationListPath(t.key));
        const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
        const tab = { key: pathname, label: '邀请函管理' };
        const next = withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname);
        safeLsSet(LS_TABS, JSON.stringify(next));
        return next;
    }

    if (isWjSurveyNewListPath(pathname)) {
        const firstIdx = prev.findIndex(t => isWjSurveyNewListPath(t.key));
        const stripped = prev.filter(t => !isWjSurveyNewListPath(t.key));
        const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
        const tab = buildWjSurveyPageTab(pathname);
        const next = withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname);
        safeLsSet(LS_TABS, JSON.stringify(next));
        return next;
    }

    if (isWjSurveyEditFlowPath(pathname)) {
        const pathId = getWjSurveyEditFlowIdFromPathname(pathname);
        let firstIdx;
        let stripped;
        if (pathId) {
            firstIdx = prev.findIndex(t => isWjSurveyEditFlowPath(t.key) && getWjSurveyEditFlowIdFromTab(t) === pathId);
            stripped = prev.filter(t => !(isWjSurveyEditFlowPath(t.key) && getWjSurveyEditFlowIdFromTab(t) === pathId));
        } else {
            firstIdx = prev.findIndex(t => isWjSurveyNewEditPath(t.key) && !getWjSurveyEditFlowIdFromTab(t));
            stripped = prev.filter(t => !(isWjSurveyNewEditPath(t.key) && !getWjSurveyEditFlowIdFromTab(t)));
        }
        const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
        const tab = buildWjSurveyEditFlowTab(pathname);
        const next = dedupeDynamicDetailTabs(withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname));
        safeLsSet(LS_TABS, JSON.stringify(next));
        return next;
    }

    if (isWjSurveyDataPath(pathname)) {
        const surveyId = getWjSurveyIdFromDataPath(pathname);
        if (surveyId) {
            const firstIdx = prev.findIndex(t => isWjSurveyDataPath(t.key) && getWjSurveyIdFromDataPath(t.key) === surveyId);
            const stripped = prev.filter(t => !(isWjSurveyDataPath(t.key) && getWjSurveyIdFromDataPath(t.key) === surveyId));
            const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
            const tab = buildWjSurveyDataTab(pathname);
            const next = dedupeDynamicDetailTabs(withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname));
            safeLsSet(LS_TABS, JSON.stringify(next));
            return next;
        }
    }

    if (isMedProjectWorkspacePath(pathname)) {
        const projectId = getMedProjectWorkspaceId(pathname);
        if (projectId) {
            const firstIdx = prev.findIndex(t => isMedProjectWorkspacePath(t.key) && getMedProjectWorkspaceId(t.key) === projectId);
            const stripped = prev.filter(t => !(isMedProjectWorkspacePath(t.key) && getMedProjectWorkspaceId(t.key) === projectId));
            const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
            const tab = buildWorkspacePageTab(pathname);
            const next = dedupeDynamicDetailTabs(withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname));
            safeLsSet(LS_TABS, JSON.stringify(next));
            return next;
        }
    }

    if (isConferenceWorkspacePath(pathname)) {
        const conferenceId = getConferenceWorkspaceId(pathname);
        if (conferenceId) {
            const firstIdx = prev.findIndex(t => isConferenceWorkspacePath(t.key) && getConferenceWorkspaceId(t.key) === conferenceId);
            const stripped = prev.filter(t => !(isConferenceWorkspacePath(t.key) && getConferenceWorkspaceId(t.key) === conferenceId));
            const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
            const tab = buildConferenceWorkspacePageTab(pathname);
            const next = dedupeDynamicDetailTabs(withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname));
            safeLsSet(LS_TABS, JSON.stringify(next));
            return next;
        }
    }

    if (isSelectionWorkspacePath(pathname)) {
        const projectId = getSelectionWorkspaceId(pathname);
        if (projectId) {
            const firstIdx = prev.findIndex(t => isSelectionWorkspacePath(t.key) && getSelectionWorkspaceId(t.key) === projectId);
            const stripped = prev.filter(t => !(isSelectionWorkspacePath(t.key) && getSelectionWorkspaceId(t.key) === projectId));
            const insertAt = firstIdx === -1 ? stripped.length : firstIdx;
            const tab = buildSelectionWorkspacePageTab(pathname);
            const next = dedupeDynamicDetailTabs(withMaxTabs([...stripped.slice(0, insertAt), tab, ...stripped.slice(insertAt)], pathname));
            safeLsSet(LS_TABS, JSON.stringify(next));
            return next;
        }
    }

    const { label, title } = resolveLabelForPathname(pathname);
    const tab = title != null ? { key: pathname, label, title } : { key: pathname, label };
    const next = withMaxTabs([...prev, tab], pathname);
    safeLsSet(LS_TABS, JSON.stringify(next));
    return next;
}

/**
 * 当前路由是否已由「同一 workspace 页签」代表（同项目，key 可能为旧子路径）。
 * @param {{ key: string }[]} tabs
 * @param {string} pathname
 */
function hasWorkspaceTabForCurrentPath(tabs, pathname) {
    if (isMedProjectWorkspacePath(pathname)) {
        const pid = getMedProjectWorkspaceId(pathname);
        if (!pid) return tabs.some(t => t.key === pathname);
        return tabs.some(t => isMedProjectWorkspacePath(t.key) && getMedProjectWorkspaceId(t.key) === pid);
    }
    if (isConferenceWorkspacePath(pathname)) {
        const cid = getConferenceWorkspaceId(pathname);
        if (!cid) return tabs.some(t => t.key === pathname);
        return tabs.some(t => isConferenceWorkspacePath(t.key) && getConferenceWorkspaceId(t.key) === cid);
    }
    if (isSelectionWorkspacePath(pathname)) {
        const sid = getSelectionWorkspaceId(pathname);
        if (!sid) return tabs.some(t => t.key === pathname);
        return tabs.some(t => isSelectionWorkspacePath(t.key) && getSelectionWorkspaceId(t.key) === sid);
    }
    if (isWjSurveyNewListPath(pathname)) {
        return tabs.some(t => isWjSurveyNewListPath(t.key));
    }
    if (isWjSurveyEditFlowPath(pathname)) {
        const pathId = getWjSurveyEditFlowIdFromPathname(pathname);
        if (pathId) {
            return tabs.some(t => isWjSurveyEditFlowPath(t.key) && getWjSurveyEditFlowIdFromTab(t) === pathId);
        }
        return tabs.some(t => isWjSurveyNewEditPath(t.key) && !getWjSurveyEditFlowIdFromTab(t));
    }
    if (isWjSurveyDataPath(pathname)) {
        const surveyId = getWjSurveyIdFromDataPath(pathname);
        if (!surveyId) return tabs.some(t => t.key === pathname);
        return tabs.some(t => isWjSurveyDataPath(t.key) && getWjSurveyIdFromDataPath(t.key) === surveyId);
    }
    return tabs.some(t => t.key === pathname);
}

/** 根据 pathname 找到 tabs 中对应的 tab 索引（workspace / 问卷场景匹配同组页签） */
function findTabIndexForPath(tabs, pathname, locationState) {
    if (isMedProjectWorkspacePath(pathname)) {
        const pid = getMedProjectWorkspaceId(pathname);
        if (pid) return tabs.findIndex(t => isMedProjectWorkspacePath(t.key) && getMedProjectWorkspaceId(t.key) === pid);
    }
    if (isConferenceWorkspacePath(pathname)) {
        const cid = getConferenceWorkspaceId(pathname);
        if (cid) return tabs.findIndex(t => isConferenceWorkspacePath(t.key) && getConferenceWorkspaceId(t.key) === cid);
    }
    if (isSelectionWorkspacePath(pathname)) {
        const sid = getSelectionWorkspaceId(pathname);
        if (sid) return tabs.findIndex(t => isSelectionWorkspacePath(t.key) && getSelectionWorkspaceId(t.key) === sid);
    }
    if (isWjSurveyNewListPath(pathname)) {
        return tabs.findIndex(t => isWjSurveyNewListPath(t.key));
    }
    if (isWjSurveyEditFlowPath(pathname)) {
        return findWjSurveyEditFlowTabIndex(tabs, pathname, locationState);
    }
    if (isWjSurveyDataPath(pathname)) {
        return findWjSurveyDataTabIndex(tabs, pathname);
    }
    return tabs.findIndex(t => t.key === pathname);
}

/** 解析当前路由应对应高亮的 PageTab key（含问卷等同组页签、workspace 页签） */
export function resolvePageTabActiveKey(tabs, pathname, locationState) {
    if (tabs.some(t => t.key === pathname)) return pathname;
    if (isWjSurveyNewListPath(pathname)) {
        const tab = tabs.find(t => isWjSurveyNewListPath(t.key));
        if (tab) return tab.key;
    }
    if (isWjSurveyEditFlowPath(pathname)) {
        const idx = findWjSurveyEditFlowTabIndex(tabs, pathname, locationState);
        if (idx >= 0) return tabs[idx].key;
    }
    if (isWjSurveyDataPath(pathname)) {
        const idx = findWjSurveyDataTabIndex(tabs, pathname);
        if (idx >= 0) return tabs[idx].key;
    }
    if (isMedProjectWorkspacePath(pathname)) {
        const pid = getMedProjectWorkspaceId(pathname);
        if (pid) {
            const tab = tabs.find(t => isMedProjectWorkspacePath(t.key) && getMedProjectWorkspaceId(t.key) === pid);
            if (tab) return tab.key;
        }
    }
    if (isConferenceWorkspacePath(pathname)) {
        const cid = getConferenceWorkspaceId(pathname);
        if (cid) {
            const tab = tabs.find(t => isConferenceWorkspacePath(t.key) && getConferenceWorkspaceId(t.key) === cid);
            if (tab) return tab.key;
        }
    }
    if (isSelectionWorkspacePath(pathname)) {
        const sid = getSelectionWorkspaceId(pathname);
        if (sid) {
            const tab = tabs.find(t => isSelectionWorkspacePath(t.key) && getSelectionWorkspaceId(t.key) === sid);
            if (tab) return tab.key;
        }
    }
    return tabs[0]?.key ?? pathname;
}

export function useSyncedPageTabs() {
    const { pathname, state: locationState } = useLocation();
    const [tabs, setTabs] = useState(() => readTabs());

    const onTabsChange = useCallback(
        next => {
            const capped = trimToMaxTabs(next, pathname);
            setTabs(capped);
            safeLsSet(LS_TABS, JSON.stringify(capped));
        },
        [pathname],
    );

    /**
     * 手动更新指定 tab 的 state。
     * @param {string} key - tab 的 key（pathname）
     * @param {object | ((prev: object | undefined) => object)} stateOrUpdater - 新 state 或更新函数
     */
    const setTabState = useCallback((key, stateOrUpdater) => {
        if (typeof key !== 'string' || !key) return;
        setTabs(prev => {
            const idx = prev.findIndex(t => t.key === key);
            if (idx === -1) return prev;
            const oldState = prev[idx].state;
            const newState = typeof stateOrUpdater === 'function' ? stateOrUpdater(oldState) : stateOrUpdater;
            if (newState === oldState) return prev;
            const updated = [...prev];
            updated[idx] = { ...updated[idx], state: newState };
            safeLsSet(LS_TABS, JSON.stringify(updated));
            return updated;
        });
    }, []);

    useEffect(() => {
        function onClosePageTab(e) {
            const key = e?.detail?.key;
            if (typeof key !== 'string' || !key) return;
            setTabs(prev => {
                if (!prev.some(t => t.key === key)) return prev;
                const next = prev.filter(t => t.key !== key);
                safeLsSet(LS_TABS, JSON.stringify(next));
                return next;
            });
        }
        window.addEventListener(PAGE_TAB_CLOSE_EVENT, onClosePageTab);
        return () => window.removeEventListener(PAGE_TAB_CLOSE_EVENT, onClosePageTab);
    }, []);

    useEffect(() => {
        function onWorkspaceTitleUpdated() {
            setTabs(prev => {
                const trimmed = trimToMaxTabs(prev, pathname);
                const { next, changed } = reapplyWorkspaceTabMeta(trimmed);
                const deduped = dedupeDynamicDetailTabs(next);
                if (!changed && deduped.length === prev.length && deduped.every((t, i) => t.key === prev[i]?.key && t.label === prev[i]?.label && (t.title ?? '') === (prev[i]?.title ?? ''))) {
                    return prev;
                }
                safeLsSet(LS_TABS, JSON.stringify(deduped));
                return deduped;
            });
        }
        window.addEventListener(WORKSPACE_TAB_TITLE_UPDATED_EVENT, onWorkspaceTitleUpdated);
        return () => window.removeEventListener(WORKSPACE_TAB_TITLE_UPDATED_EVENT, onWorkspaceTitleUpdated);
    }, [pathname]);

    useEffect(() => {
        if (EXCLUDED_PATHS.has(pathname)) return;

        setTabs(prev => {
            const trimmed = trimToMaxTabs(prev, pathname);
            const deduped = dedupeDynamicDetailTabs(trimmed);

            if (isMedProjectWorkspacePath(pathname)) {
                const pid = getMedProjectWorkspaceId(pathname);
                if (pid != null) {
                    const wi = deduped.findIndex(t => isMedProjectWorkspacePath(t.key) && getMedProjectWorkspaceId(t.key) === pid);
                    if (wi >= 0) {
                        const built = buildWorkspacePageTab(pathname);
                        const old = deduped[wi];
                        if (old.key !== built.key || old.label !== built.label || (old.title ?? '') !== (built.title ?? '')) {
                            const updated = [...deduped.slice(0, wi), built, ...deduped.slice(wi + 1)];
                            const nextDeduped = dedupeDynamicDetailTabs(updated);
                            safeLsSet(LS_TABS, JSON.stringify(nextDeduped));
                            return nextDeduped;
                        }
                    }
                }
            }

            if (isConferenceWorkspacePath(pathname)) {
                const cid = getConferenceWorkspaceId(pathname);
                if (cid != null) {
                    const wi = deduped.findIndex(t => isConferenceWorkspacePath(t.key) && getConferenceWorkspaceId(t.key) === cid);
                    if (wi >= 0) {
                        const built = buildConferenceWorkspacePageTab(pathname);
                        const old = deduped[wi];
                        if (old.key !== built.key || old.label !== built.label || (old.title ?? '') !== (built.title ?? '')) {
                            const updated = [...deduped.slice(0, wi), built, ...deduped.slice(wi + 1)];
                            const nextDeduped = dedupeDynamicDetailTabs(updated);
                            safeLsSet(LS_TABS, JSON.stringify(nextDeduped));
                            return nextDeduped;
                        }
                    }
                }
            }

            if (isSelectionWorkspacePath(pathname)) {
                const sid = getSelectionWorkspaceId(pathname);
                if (sid != null) {
                    const wi = deduped.findIndex(t => isSelectionWorkspacePath(t.key) && getSelectionWorkspaceId(t.key) === sid);
                    if (wi >= 0) {
                        const built = buildSelectionWorkspacePageTab(pathname);
                        const old = deduped[wi];
                        if (old.key !== built.key || old.label !== built.label || (old.title ?? '') !== (built.title ?? '')) {
                            const updated = [...deduped.slice(0, wi), built, ...deduped.slice(wi + 1)];
                            const nextDeduped = dedupeDynamicDetailTabs(updated);
                            safeLsSet(LS_TABS, JSON.stringify(nextDeduped));
                            return nextDeduped;
                        }
                    }
                }
            }

            if (isWjSurveyEditFlowPath(pathname)) {
                const wi = findWjSurveyEditFlowTabIndex(deduped, pathname, locationState);
                if (wi >= 0) {
                    const old = deduped[wi];
                    const mergedState = old.state ?? locationState;
                    const built = buildWjSurveyEditFlowTab(pathname, mergedState);
                    if (old.key !== built.key || old.label !== built.label || (old.title ?? '') !== (built.title ?? '')) {
                        const updated = [...deduped.slice(0, wi), { ...built, state: mergedState }, ...deduped.slice(wi + 1)];
                        const nextDeduped = dedupeDynamicDetailTabs(updated);
                        safeLsSet(LS_TABS, JSON.stringify(nextDeduped));
                        return nextDeduped;
                    }
                }
            }

            if (isWjSurveyDataPath(pathname)) {
                const wi = findWjSurveyDataTabIndex(deduped, pathname);
                if (wi >= 0) {
                    const old = deduped[wi];
                    const mergedState = old.state ?? locationState;
                    const built = buildWjSurveyDataTab(pathname, mergedState);
                    if (old.key !== built.key || old.label !== built.label || (old.title ?? '') !== (built.title ?? '')) {
                        const updated = [...deduped.slice(0, wi), { ...built, state: mergedState }, ...deduped.slice(wi + 1)];
                        const nextDeduped = dedupeDynamicDetailTabs(updated);
                        safeLsSet(LS_TABS, JSON.stringify(nextDeduped));
                        return nextDeduped;
                    }
                }
            }

            if (isEnterpriseStaffFormPath(pathname)) {
                const wi = deduped.findIndex(t => t.key === pathname);
                if (wi >= 0) {
                    const old = deduped[wi];
                    const mergedState = old.state ?? locationState;
                    const built = buildEnterpriseStaffFormTab(pathname, mergedState);
                    if (old.key !== built.key || old.label !== built.label || (old.title ?? '') !== (built.title ?? '')) {
                        const updated = [...deduped.slice(0, wi), { ...built, state: mergedState }, ...deduped.slice(wi + 1)];
                        const nextDeduped = dedupeDynamicDetailTabs(updated);
                        safeLsSet(LS_TABS, JSON.stringify(nextDeduped));
                        return nextDeduped;
                    }
                }
            }

            const trimmedPersist = deduped.length !== prev.length || deduped.some((t, i) => t.key !== prev[i]?.key || t.label !== prev[i]?.label || (t.title ?? '') !== (prev[i]?.title ?? ''));
            if (trimmedPersist) safeLsSet(LS_TABS, JSON.stringify(deduped));

            if (hasWorkspaceTabForCurrentPath(deduped, pathname) || hasWjSurveyTabForCurrentPath(deduped, pathname, locationState)) {
                return trimmedPersist ? deduped : prev;
            }
            return nextTabsForPathname(deduped, pathname);
        });
    }, [pathname, locationState]);

    /* 将 location.state 同步到当前 tab 的 state 字段 */
    useEffect(() => {
        if (EXCLUDED_PATHS.has(pathname)) return;
        if (locationState == null || typeof locationState !== 'object' || Object.keys(locationState).length === 0) return;

        setTabs(prev => {
            const idx = findTabIndexForPath(prev, pathname, locationState);
            if (idx === -1) return prev;
            const existing = prev[idx].state;
            const surveyName = extractSurveyNameFromState(locationState);
            const staffName = extractStaffNameFromState(locationState);
            const shouldRefreshWjMeta = isWjSurveyNewListPath(pathname) || isWjSurveyEditFlowPath(pathname) || isWjSurveyDataPath(pathname);
            const shouldRefreshStaffMeta = isEnterpriseStaffFormPath(pathname);
            const nextTitle = surveyName ?? staffName ?? prev[idx].title;
            const nextLabel = shouldRefreshStaffMeta ? resolveEnterpriseStaffFormLabel(locationState, pathname) : shouldRefreshWjMeta ? (resolveWjSurveyPageLabel(pathname) ?? prev[idx].label) : prev[idx].label;
            // 浅比较：key 集合与值均相同则跳过
            if (existing != null && typeof existing === 'object') {
                const keys = Object.keys(locationState);
                const stateUnchanged = keys.length === Object.keys(existing).length && keys.every(k => existing[k] === locationState[k]);
                const metaUnchanged = (prev[idx].title ?? '') === (nextTitle ?? '') && prev[idx].label === nextLabel;
                if (stateUnchanged && metaUnchanged) {
                    return prev;
                }
            }
            const updated = [...prev];
            updated[idx] = {
                ...updated[idx],
                state: locationState,
                label: nextLabel,
                ...(nextTitle ? { title: nextTitle } : {}),
            };
            safeLsSet(LS_TABS, JSON.stringify(updated));
            return updated;
        });
    }, [pathname, locationState]);

    return { tabs, onTabsChange, setTabState };
}
